<script setup>
import Icon from '@components/Icon.vue'
import BaseModal from '@components/BaseModal.vue'
import ListingOptions from '@components/ListingOptions.vue'
import ListingFrequency from '@components/ListingFrequency.vue'
import ListingQuantity from '@components/ListingQuantity.vue'
import AddToCartButton from '@components/AddToCartButton.vue'
import { $selectedImageIndex } from '../store'
import {computed, ref, onMounted, watch} from 'vue'
import { formatPrice } from '@helpers/utils'
import Image from '@components/Image.vue'
import sezzleBanner from '@images/Sezzle-Banner.png'
import sezzleLogoLarge from '@images/Sezzle-Purple-Logo-Large.png'
import sezzleLogo from '@images/Sezzle-Purple-Logo.png'

const props = defineProps({
  listing: {
    type: Object,
    required: true,
  },
  flavorOptions: {
    type: Object,
    default: () => {},
  },
})

const selectedProduct = ref(props.listing?.products?.[0])

const selectedListingPlan = ref(props.listing?.plans?.[0])
const purchaseType = ref('once')
const quantity = ref(1)
const sezzleOpen = ref(false)

const savings = computed(() => {
  return Math.round(((selectedProduct.value.compare_at_price - selectedProduct.value.price) / selectedProduct.value.compare_at_price) * 100)
})

const addToCartPayload = computed(() => {
  return {
    product_id: selectedProduct.value.id,
    listing_plan_id: purchaseType.value === 'sub' ? selectedListingPlan.value?.id : null,
    quantity: quantity.value,
  }
})

const outOfStock = computed(() => !selectedProduct.value.available_for_sale)

watch(selectedProduct, () => {
  let index = props.listing.images.findIndex(({ id }) => id === selectedProduct.value.listing_image_id)
  if (index !== -1) {
    $selectedImageIndex.set(index)
  }
})

onMounted(() => {
  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({
    event: 'view_item',
    ecommerce: {
      currency: 'USD',
      items: [{
        item_id: props.listing.id.toString(),
        item_name: props.listing.title,
        item_brand: props.listing?.product_line?.brand?.title || '',
        item_list_id: 'pdp',
        item_list_name: 'pdp',
        price: Number(selectedProduct.value.price).toString(),
        quantity: '1',
      }]
    },
  })

  window?.sendFbEvent('ViewContent', {
    currency: 'USD',
    value: selectedProduct.value?.price || 0,
    content_type: 'product_group',
    content_ids: [props.listing?.id?.toString()],
    contents: [{
      id: props.listing?.id?.toString(),
      item_price: selectedProduct.value?.price || 0,
      quantity: 1,
    }],
  })

  window._learnq = window?._learnq || []
  window._learnq.push([
    'track',
    'Viewed Product',
    {
      ProductName: props.listing.title,
      ProductID: props.listing.id,
      ImageURL: props.listing?.image_url,
      URL: props.listing.url,
      Brand: props.listing?.product_line?.brand?.title || '',
      Price: selectedProduct.value.price,
    },
  ])

  window.criteo_q = window?.criteo_q || [];
  window.criteo_q.push(
      { event: "setAccount", account: 94147 },
      { event: "setSiteType", type: /iPad/.test(navigator.userAgent) ? "t" : /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(navigator.userAgent) ? "m" : "d"},
      { event: "viewItem", item: props.listing.id }
  );
})
</script>

<template>
  <div>
    <ListingOptions
        :listing="listing"
        :flavors="flavorOptions"
        v-model:selected-product="selectedProduct"
    />

    <div v-if="savings > 0" class="product__savings">{{ savings }}% Off</div>

    <ListingFrequency
        v-if="!outOfStock"
        :listing="listing"
        :quantity="quantity"
        v-model:purchase-type="purchaseType"
        v-model:selected-listing-plan="selectedListingPlan"
        :selected-product="selectedProduct"
    />
  </div>
  <div class="product__actions">
    <ListingQuantity v-if="!outOfStock" v-model="quantity" />
    <AddToCartButton class="product__add-to-cart" :listing="listing" :payload="addToCartPayload" :disabled="outOfStock">
      Add To Cart
    </AddToCartButton>
  </div>

  <div v-if="purchaseType === 'once' && selectedProduct?.price >= 20 && !outOfStock" class="product__sezzle">
    <span>or 4 interest-free payments of <strong>${{ formatPrice(selectedProduct?.price / 4) }}</strong> with </span>
    <a href="#" @click.prevent="sezzleOpen = true">
      <Image :src="sezzleLogo" alt="Sezzle" loading="lazy" />
    </a>
  </div>

  <div class="product__bar" id="actionBar">
    <span class="product__bar-title">{{listing.title}}</span>
    <div class="product__actions">
      <template v-if="!outOfStock">
        <ListingQuantity v-model="quantity" />
      </template>
      <AddToCartButton class="product__add-to-cart" :listing="listing" :payload="addToCartPayload" :disabled="outOfStock || selectedProduct.price === null">Add To Cart</AddToCartButton>
    </div>
  </div>

  <BaseModal v-model="sezzleOpen" :max-width="500" :dismissable="false">
    <div class="sezzle-info">
      <div class="sezzle-info__title">
        <Image :src="sezzleLogoLarge" alt="Sezzle" loading="lazy" />
        <a href="#" @click.prevent="sezzleOpen = false">
          <Icon type="close-circle" />
        </a>
      </div>
      <Image :src="sezzleBanner" alt="Sezzle" loading="lazy" />
      <p>Pay in 4 interest-free payments</p>
    </div>
  </BaseModal>
</template>

<style lang="scss" scoped>
.product__savings {
  border-radius: 6px;
  background: rgba(215, 88, 88, 0.15);
  padding: 4px 10px;
  color: var(--Watermelon, #D75858);
  font-family: Outfit, 'Outfit Fallback', Arial, serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 17.6px; /* 125.714% */
  text-align: center;
  margin-top: 16px;
  font-style: italic;
}

.product__actions {
  display: flex;
  align-items: center;
  margin-top: 16px;
  gap: 8px;

  :deep(.product__add-to-cart) {
    flex-grow: 1;
    border-radius: 24px;
    background: var(--Gradient-Linear-07, linear-gradient(78deg, #C76848 -0.11%, #F9C74F 99.89%));
    color: var(--shades-white, #FFF);
    text-align: center;
    font-family: Outfit, 'Outfit Fallback', Arial, serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px; /* 125% */
    text-transform: uppercase;
    border: 0;
    padding: 14px 18px;
    cursor: pointer;

    &:hover {
      background: var(--Light-orange, #EFA670);
    }
  }
}

.sezzle-info {
  .sezzle-info__title {
    display: flex;
    align-items: center;
    justify-content: space-between;

    img {
      height: auto;
      width: 114px;

      @media screen and (min-width: 768px) {
        width: 162px;
      }
    }
  }

  img {
    width: 100%;
    display: block;
    height: auto;
  }

  p {
    color: var(--Neutral-800, #393632);
    text-align: center;
    font-family: Outfit, 'Outfit Fallback', Arial, serif;
    font-size: 20px;
    font-weight: 400;
    line-height: 24px; /* 120% */
  }
}

.product__sezzle {
  display: flex;
  align-items: center;
  color: var(--Neutral-1000, #3C3833);
  font-family: Poppins, 'Poppins Fallback', 'Trebuchet MS Fallback', Arial, serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 28px; /* 175% */
  margin-top: 8px;

  @media screen and (min-width: 768px) {
    font-size: 16px;
  }

  a {
    text-decoration: none;
  }

  img {
    display: block;
    width: 66px;
    height: auto;
    margin-left: 4px;

    @media screen and (min-width: 768px) {
      width: 80px;
    }
  }
}

.product__bar {
  display: block;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9;
  padding: 8px 20px;
  background: var(--shades-white, #FFF);
  box-shadow: 5px 0 16px 0 rgba(0, 0, 0, 0.25);

  @media screen and (min-width: 768px) {
    display: none !important;
  }

  .product__bar-title {
    display: block;
    color: var(--Neutral-1000, #3C3833);
    text-align: center;
    font-family: Outfit, 'Outfit Fallback', Arial, serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px; /* 150% */
    margin-bottom: 6px;
  }
}
</style>
